import { MARKETING_CONFIG } from "@/config/marketing";
import { cn } from "@/lib/utils";

/**
 * Official-spec App Store + Google Play badges.
 *
 * Apple HIG: use the official "Download on the App Store" lockup. Minimum
 * height 40px, clear-space = badge corner radius.
 * Google brand: use the official "Get it on Google Play" lockup at the same
 * height as the Apple badge.
 *
 * URLs are sourced from MARKETING_CONFIG.appStore. Both stores are live, so
 * each badge is always a real click-through opening in a new tab.
 */

interface AppStoreBadgesOfficialProps {
  className?: string;
  /** Badge height in px (Apple HIG minimum 40px). */
  height?: number;
}

const AppStoreBadge = ({ height }: { height: number }) => (
  // Apple's "Download on the App Store" - black variant per HIG
  <svg
    role="img"
    aria-label="Download on the App Store"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 120 40"
    style={{ height, width: "auto" }}
    className="block"
  >
    <title>Download on the App Store</title>
    <rect width="120" height="40" rx="6" fill="#000" />
    <rect
      x="0.5"
      y="0.5"
      width="119"
      height="39"
      rx="5.5"
      fill="none"
      stroke="#A6A6A6"
      strokeWidth="1"
    />
    {/* Apple logo */}
    <path
      fill="#fff"
      d="M22.66 20.36c-.02-2.16 1.77-3.21 1.85-3.26-1.01-1.47-2.58-1.67-3.14-1.69-1.34-.14-2.61.79-3.29.79-.69 0-1.73-.78-2.85-.75-1.47.02-2.82.85-3.58 2.16-1.53 2.65-.39 6.57 1.1 8.72.73 1.05 1.59 2.23 2.72 2.19 1.1-.04 1.51-.71 2.84-.71s1.7.71 2.86.69c1.18-.02 1.93-1.07 2.65-2.13.84-1.22 1.18-2.41 1.2-2.47-.03-.01-2.28-.87-2.36-3.54zm-2.17-6.5c.6-.74 1.01-1.76.9-2.78-.87.04-1.93.59-2.55 1.31-.55.64-1.04 1.69-.91 2.69.97.07 1.95-.49 2.56-1.22z"
    />
    {/* Text */}
    <text
      x="34"
      y="16"
      fill="#fff"
      fontFamily="-apple-system, BlinkMacSystemFont, 'Helvetica Neue', Helvetica, Arial, sans-serif"
      fontSize="7"
    >
      Download on the
    </text>
    <text
      x="34"
      y="30"
      fill="#fff"
      fontFamily="-apple-system, BlinkMacSystemFont, 'Helvetica Neue', Helvetica, Arial, sans-serif"
      fontSize="14"
      fontWeight="600"
    >
      App Store
    </text>
  </svg>
);

const GooglePlayBadge = ({ height }: { height: number }) => (
  // Google's "Get it on Google Play" - black variant
  <svg
    role="img"
    aria-label="Get it on Google Play"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 135 40"
    style={{ height, width: "auto" }}
    className="block"
  >
    <title>Get it on Google Play</title>
    <rect width="135" height="40" rx="6" fill="#000" />
    <rect
      x="0.5"
      y="0.5"
      width="134"
      height="39"
      rx="5.5"
      fill="none"
      stroke="#A6A6A6"
      strokeWidth="1"
    />
    {/* Play triangle (multicolor) */}
    <g transform="translate(11 8)">
      <path d="M0.6 0.5C0.3 0.8 0.2 1.3 0.2 1.9V22.1c0 .6.1 1.1.4 1.4l.1.1L11.9 12.1v-.2L0.7 .5z" fill="#5BC9F4"/>
      <path d="M15.6 15.9l-3.7-3.7v-.3l3.7-3.7.1.1 4.4 2.5c1.3.7 1.3 1.9 0 2.6l-4.5 2.5z" fill="#FEE000"/>
      <path d="M15.7 15.8L11.9 12 .6 23.3c.4.4 1.1.5 1.9.1l13.2-7.6" fill="#EA4335"/>
      <path d="M15.7 8.2L2.5.6C1.7.2 1 .3.6.7L11.9 12l3.8-3.8z" fill="#34A853"/>
    </g>
    {/* Text */}
    <text
      x="40"
      y="16"
      fill="#fff"
      fontFamily="-apple-system, BlinkMacSystemFont, 'Roboto', Helvetica, Arial, sans-serif"
      fontSize="7"
    >
      GET IT ON
    </text>
    <text
      x="40"
      y="30"
      fill="#fff"
      fontFamily="-apple-system, BlinkMacSystemFont, 'Roboto', Helvetica, Arial, sans-serif"
      fontSize="14"
      fontWeight="600"
    >
      Google Play
    </text>
  </svg>
);

export function AppStoreBadgesOfficial({
  className,
  height = 48,
}: AppStoreBadgesOfficialProps) {
  const { iosUrl, androidUrl } = MARKETING_CONFIG.appStore;

  return (
    <div
      className={cn(
        "mx-auto flex w-full flex-wrap items-center justify-center gap-3 text-center sm:gap-4",
        className
      )}
    >
      <a
        href={iosUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Download elefa Connect on the App Store"
        className="rounded-md transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <AppStoreBadge height={height} />
      </a>

      <a
        href={androidUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Get elefa Connect on Google Play"
        className="rounded-md transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <GooglePlayBadge height={height} />
      </a>
    </div>
  );
}
